// The api's reason for refusing a write, as plain text for a notification.
//
// A refused send used to show "There was an error sending mail" whatever the
// reason, and nothing at all when the body was not JSON: the composer stayed
// on "Sending" for good. The api says why in a few shapes (DRF validation
// errors and the send hook's own): {pre_save_hook: '...'}, the same as a
// list, {__all__: {pre_save_hook: ...}}, {__all__: [...]}, {detail: '...'}.
//
// The result is a string for React to render as text. It must never reach
// the page as HTML: the reason can quote what a sender or recipient typed.

// Where the api puts a reason meant for the person, most specific first.
// Field errors ({email: [...]}) are left out: they name a form field this
// app does not show.
const REASON_KEYS = ['pre_save_hook', 'post_save_hook', '__all__', 'non_field_errors', 'detail'];
const MAX_DEPTH = 4;
const MAX_LENGTH = 300;
// The hook runner prefixes every refusal with its own name.
const HOOK_PREFIX = /^Error running (?:pre|post)_save_hook:\s*/i;
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f-\u009f]/g;

const firstReason = (value, depth) => {
  if (depth > MAX_DEPTH || value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) {
    for (const item of value) {
      const reason = firstReason(item, depth + 1);
      if (reason) return reason;
    }
    return '';
  }
  if (typeof value === 'object') {
    for (const key of REASON_KEYS) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        const reason = firstReason(value[key], depth + 1);
        if (reason) return reason;
      }
    }
  }
  return '';
};

// '' when the body carries no reason the app can show.
export const apiErrorMessage = (body) => {
  if (body === null || typeof body !== 'object') return '';
  const text = firstReason(body, 0)
    .replace(HOOK_PREFIX, '')
    .replace(CONTROL, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > MAX_LENGTH ? `${text.slice(0, MAX_LENGTH - 1)}…` : text;
};

// The JSON object of a response, or {} when there is none: a proxy's HTML
// error page, an empty body. A caller then still hears that a request failed
// instead of the parse error escaping and the failure going unseen.
export const jsonBody = (res) => res.json().then(
  (json) => (json !== null && typeof json === 'object' ? json : {}),
  () => ({}),
);
